import React, { useState, useEffect, memo, useCallback } from 'react';
import {
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  Typography,
} from '@mui/material';
import PlanInfoCard from './PlanInfoCard';
import BillingHistoryList from './BillingHistoryList';
import PaymentMethodManager from './PaymentMethodManager';
import BillingService from '../../../services/billingService';
import { useStore } from '../../../store/useStore';
import type { SalonHubBilling } from '../../../types/billing';
import type { ComprehensiveSettings } from '../../../types/settings';

interface AccountManagementTabProps {
  settings?: ComprehensiveSettings;
  updateSettings?: (section: keyof ComprehensiveSettings, updates: any) => Promise<void>;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
}

const AccountManagementTab: React.FC<AccountManagementTabProps> = ({
  settings: _settings,
  updateSettings: _updateSettings,
  onError,
  onSuccess,
}) => {
  const currentPlan = useStore((state) => state.currentPlan); // Zustandストアから現在のプランを監視
  const [billingInfo, setBillingInfo] = useState<SalonHubBilling | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Snackbar state for internal notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // SalonHub課金情報の取得
  const fetchBillingInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await BillingService.getBillingInfo();
      setBillingInfo(data);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'SalonHub課金情報の取得中にエラーが発生しました';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPlan, onError]); // eslint-disable-line react-hooks/exhaustive-deps

  // 初回読み込みとプラン変更時の再読み込み
  useEffect(() => {
    fetchBillingInfo();
  }, [currentPlan, fetchBillingInfo]); // currentPlanが変更されたら再読み込み

  // プランアップグレード処理
  const handleUpgrade = async () => {
    try {
      // プラン変更ダイアログやページに遷移
      console.log('プランアップグレード処理開始');
      handleNotification('プランアップグレード機能は実装中です', 'info');
    } catch (err) {
      const errorMessage = 'プランアップグレードに失敗しました';
      onError?.(errorMessage);
    }
  };

  // プラン変更処理
  const handleChangePlan = async () => {
    try {
      console.log('プラン変更処理開始');
      handleNotification('プラン変更機能は実装中です', 'info');
    } catch (err) {
      const errorMessage = 'プラン変更に失敗しました';
      onError?.(errorMessage);
    }
  };

  // 内部通知処理
  const handleNotification = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity as 'success' | 'error');
    setSnackbarOpen(true);
    
    if (severity === 'success') {
      onSuccess?.(message);
    } else if (severity === 'error') {
      onError?.(message);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }}>SalonHub課金情報を読み込み中...</Typography>
      </Box>
    );
  }

  if (error || !billingInfo) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Box>
              <Typography variant="button" onClick={fetchBillingInfo} sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
                再試行
              </Typography>
            </Box>
          }
        >
          {error || 'SalonHub課金情報を取得できませんでした'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        SalonHubアカウント管理
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* プラン情報と使用状況 */}
        <PlanInfoCard
          currentPlan={billingInfo.currentPlan}
          usage={billingInfo.usage}
          onUpgrade={handleUpgrade}
          onChangePlan={handleChangePlan}
        />

        {/* 支払い方法管理 */}
        <PaymentMethodManager
          onError={(error) => handleNotification(error, 'error')}
          onSuccess={(message) => handleNotification(message, 'success')}
        />

        {/* 請求履歴 */}
        <BillingHistoryList
          onError={(error) => handleNotification(error, 'error')}
        />
      </Box>

      {/* Internal Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default memo(AccountManagementTab);